<template>
	<div class="municipality-report-container container">
		<!-- Header -->
		<div class="page-header d-flex align-items-center my-4">
			<router-link :to="{ name: 'autoCampaignRunReport', params: { runId: $route.params.runId } }" class="back-btn me-3" title="Back to Run Report">
				<i class="fad fa-arrow-left"></i>
			</router-link>
			<div class="flex-grow-1">
				<h2 class="page-title">{{ item.name || 'Mail Piece Report' }}</h2>
				<p class="page-subtitle">
					<span v-if="campaign.name">{{ campaign.name }}</span>
					<span v-if="run.code"> · {{ run.code }}</span>
				</p>
			</div>
			<div class="header-actions">
				<span v-if="item.status" class="status-badge" :class="item.status">{{ item.status }}</span>
				<a v-if="item.lob_url" :href="item.lob_url" target="_blank" rel="noopener" class="header-btn" :title="item.lob_id">
					<i class="fad fa-eye"></i> View on LOB
				</a>
				<button class="header-btn" :disabled="loading" @click="load">
					<i class="fad fa-sync" :class="{ 'fa-spin': loading }"></i> Refresh
				</button>
			</div>
		</div>

		<!-- Loading -->
		<div v-if="loading && !loaded" class="loading-state">
			<i class="fad fa-spinner-third fa-spin"></i>
			<p>Loading mail piece from LOB…</p>
		</div>

		<div v-else-if="loadError" class="alert alert-danger">{{ loadError }}</div>

		<template v-else>
			<div v-if="lobError" class="alert alert-warning">
				<i class="fad fa-exclamation-triangle me-2"></i>
				Could not reach LOB, so only our own records are shown. <span class="lob-error-detail">{{ lobError }}</span>
			</div>

			<div class="row">
				<!-- Left column — what was actually mailed -->
				<div class="col-lg-5">
					<div class="preview-container">
						<h3 class="preview-title">Mail Piece</h3>

						<div v-if="thumbnails.length" class="thumb-list">
							<div v-for="(thumb, index) in thumbnails" :key="index" class="thumb-wrapper">
								<div class="thumb-label">{{ thumbLabel(index) }}</div>
								<a :href="thumb.large" target="_blank" rel="noopener" class="thumb-link" title="Open full size">
									<img :src="thumb.medium" :alt="thumbLabel(index)" />
								</a>
							</div>
						</div>

						<div v-else class="image-placeholder">
							<div class="placeholder-content">
								<i class="fad fa-image" style="font-size: 2rem;"></i>
								<p>No preview available from LOB</p>
							</div>
						</div>

						<div v-if="lob.url || lob.raw_url" class="pdf-actions">
							<a v-if="lob.url" :href="lob.url" target="_blank" rel="noopener" class="btn btn-sm btn-outline-primary">
								<i class="fad fa-file-pdf"></i> Print-ready PDF
							</a>
							<a v-if="lob.raw_url" :href="lob.raw_url" target="_blank" rel="noopener" class="btn btn-sm btn-outline-secondary">
								<i class="fad fa-file-alt"></i> Raw PDF
							</a>
						</div>
						<p v-if="lob.url" class="asset-note">LOB asset links are signed and expire after a while.</p>
					</div>
				</div>

				<!-- Right column — everything we know -->
				<div class="col-lg-7">
					<div class="data-container">
						<!-- Delivery timeline -->
						<div class="data-section mb-4">
							<div class="section-header">
								<h3 class="section-title"><i class="fad fa-truck me-2"></i> Delivery Timeline</h3>
							</div>
							<div class="section-content">
								<ul v-if="trackingEvents.length" class="timeline">
									<li v-for="event in trackingEvents" :key="event.id" class="timeline-item" :class="eventClass(event.name)">
										<div class="timeline-dot"></div>
										<div class="timeline-body">
											<div class="timeline-name">{{ event.name }}</div>
											<div class="timeline-meta">
												{{ formatDateTime(event.time || event.date_created) }}
												<span v-if="event.location"> · {{ event.location }}</span>
											</div>
											<div v-if="event.details" class="timeline-details">{{ event.details }}</div>
										</div>
									</li>
								</ul>
								<p v-else class="empty-note">
									LOB has no tracking events yet. USPS usually reports the first scan within two business days of the send date.
								</p>
							</div>
						</div>

						<!-- Our delivery record -->
						<div class="data-section mb-4">
							<div class="section-header">
								<h3 class="section-title"><i class="fad fa-clipboard-check me-2"></i> Delivery Status</h3>
							</div>
							<div class="section-content">
								<div class="info-grid">
									<div class="info-group">
										<label class="info-label">Our Status</label>
										<div class="info-value"><span class="status-badge" :class="item.status">{{ item.status }}</span></div>
									</div>
									<div class="info-group">
										<label class="info-label">LOB Status</label>
										<div class="info-value">{{ lob.status || '—' }}</div>
									</div>
									<div class="info-group">
										<label class="info-label">Delivered</label>
										<div class="info-value">
											<span v-if="item.delivered_at" :title="item.delivery_assumed ? 'Estimated from the expected delivery date — LOB never reported a delivery event' : ''">
												<span v-if="item.delivery_assumed">~</span>{{ item.delivery_assumed ? formatDate(item.delivered_at) : formatDateTime(item.delivered_at) }}
												<small v-if="item.delivery_assumed" class="text-muted">(estimated)</small>
											</span>
											<span v-else>—</span>
										</div>
									</div>
									<div class="info-group">
										<label class="info-label">Expected Delivery</label>
										<div class="info-value">{{ formatDate(item.expected_delivery_at) }}</div>
									</div>
									<div class="info-group">
										<label class="info-label">Sent</label>
										<div class="info-value">{{ formatDateTime(item.send_at) }}</div>
									</div>
									<div class="info-group">
										<label class="info-label">Returned</label>
										<div class="info-value">{{ formatDateTime(item.returned_at) }}</div>
									</div>
									<div class="info-group">
										<label class="info-label">Last Checked</label>
										<div class="info-value">{{ formatDateTime(item.last_status_check_at) }}</div>
									</div>
									<div class="info-group">
										<label class="info-label">Cost</label>
										<div class="info-value text-success">{{ itemCost }}</div>
									</div>
								</div>
								<div v-if="item.error" class="error-box">
									<strong>Error:</strong> {{ item.error }}
								</div>
								<div v-if="lob.failure_reason" class="error-box">
									<strong>LOB failure reason:</strong> {{ lob.failure_reason }}
								</div>
							</div>
						</div>

						<!-- Mail piece details -->
						<div class="data-section mb-4">
							<div class="section-header">
								<h3 class="section-title"><i class="fad fa-mail-bulk me-2"></i> Mail Piece Details</h3>
							</div>
							<div class="section-content">
								<div class="info-grid">
									<div class="info-group">
										<label class="info-label">LOB ID</label>
										<div class="info-value font-monospace">{{ item.lob_id || '—' }}</div>
									</div>
									<div class="info-group">
										<label class="info-label">Type</label>
										<div class="info-value text-capitalize">{{ lob.object || campaign.type || '—' }}</div>
									</div>
									<div class="info-group">
										<label class="info-label">Size</label>
										<div class="info-value">{{ lob.size || item.p_size || '—' }}</div>
									</div>
									<div class="info-group">
										<label class="info-label">Mail Type</label>
										<div class="info-value">{{ lob.mail_type || item.mail_type || '—' }}</div>
									</div>
									<div class="info-group">
										<label class="info-label">Carrier</label>
										<div class="info-value">{{ lob.carrier || item.carrier || '—' }}</div>
									</div>
									<div class="info-group">
										<label class="info-label">Use Type</label>
										<div class="info-value">{{ lob.use_type || '—' }}</div>
									</div>
									<div class="info-group">
										<label class="info-label">Print Speed</label>
										<div class="info-value">{{ lob.print_speed || '—' }}</div>
									</div>
									<div class="info-group">
										<label class="info-label">Created at LOB</label>
										<div class="info-value">{{ formatDateTime(lob.date_created) }}</div>
									</div>
									<div class="info-group">
										<label class="info-label">Last Modified</label>
										<div class="info-value">{{ formatDateTime(lob.date_modified) }}</div>
									</div>
									<div class="info-group">
										<label class="info-label">Completed</label>
										<div class="info-value">{{ formatDateTime(lob.completed_at) }}</div>
									</div>
									<div class="info-group">
										<label class="info-label">Expunged On</label>
										<div class="info-value">{{ formatDate(lob.to_be_expunged_date) }}</div>
									</div>
									<div class="info-group">
										<label class="info-label">Description</label>
										<div class="info-value">{{ lob.description || item.description || '—' }}</div>
									</div>
								</div>
							</div>
						</div>

						<!-- Addresses -->
						<div class="data-section mb-4">
							<div class="section-header">
								<h3 class="section-title"><i class="fad fa-address-card me-2"></i> Addresses</h3>
							</div>
							<div class="section-content">
								<div v-if="recipientMoved" class="alert alert-warning py-2 px-3 mb-3">
									<i class="fad fa-exclamation-triangle me-2"></i> LOB flagged this recipient as moved.
								</div>
								<div class="row">
									<div class="col-md-6">
										<label class="info-label">Recipient</label>
										<address class="address-block">
											<strong>{{ addressTo.name || item.name }}</strong><br />
											<span v-if="addressTo.company">{{ addressTo.company }}<br /></span>
											{{ addressTo.address_line1 || item.address }}<br />
											<span v-if="addressTo.address_line2">{{ addressTo.address_line2 }}<br /></span>
											{{ addressTo.address_city || item.city }}, {{ addressTo.address_state || item.state }}
											{{ addressTo.address_zip || item.zip }}
										</address>
									</div>
									<div class="col-md-6">
										<label class="info-label">Sender</label>
										<address class="address-block">
											<strong>{{ addressFrom.name || '—' }}</strong><br />
											<span v-if="addressFrom.company">{{ addressFrom.company }}<br /></span>
											{{ addressFrom.address_line1 }}<br />
											<span v-if="addressFrom.address_line2">{{ addressFrom.address_line2 }}<br /></span>
											{{ addressFrom.address_city }}, {{ addressFrom.address_state }} {{ addressFrom.address_zip }}
										</address>
									</div>
								</div>
							</div>
						</div>

						<!-- Unique URL tracking -->
						<div v-if="item.unique_url" class="data-section mb-4">
							<div class="section-header">
								<h3 class="section-title"><i class="fad fa-link me-2"></i> Unique URL</h3>
							</div>
							<div class="section-content">
								<div class="info-group">
									<label class="info-label">Link</label>
									<div class="info-value">
										<code>{{ item.unique_url }}</code>
									</div>
								</div>
								<div class="info-group">
									<label class="info-label">Visited</label>
									<div class="info-value">
										<span v-if="item.unique_url_visited_at" class="text-success">
											<i class="fad fa-check-circle"></i> {{ formatDateTime(item.unique_url_visited_at) }}
											<span v-if="item.unique_url_visit_info && item.unique_url_visit_info.channel" class="text-muted ms-1">
												({{ item.unique_url_visit_info.channel === 'qr' ? 'QR scan' : 'Typed link' }})
											</span>
											<div v-if="item.unique_url_visits > 1" class="text-muted small">
												{{ item.unique_url_visits }} visits · last {{ formatDateTime(item.unique_url_last_visited_at) }}
											</div>
										</span>
										<span v-else class="text-muted">Not visited</span>
									</div>
								</div>
								<div v-if="item.unique_url_visit_info" class="info-group">
									<label class="info-label">Visit Info</label>
									<pre class="raw-json">{{ item.unique_url_visit_info }}</pre>
								</div>
								<div class="info-group">
									<label class="info-label">Visit timeline</label>
									<ShortlinkVisitTimeline type="auto_item" :id="item.id" />
								</div>
								</div>
								</div>

						<!-- Municipality -->
						<div v-if="municipality" class="data-section mb-4">
							<div class="section-header">
								<h3 class="section-title"><i class="fad fa-map-marker-alt me-2"></i> Municipality</h3>
							</div>
							<div class="section-content">
								<div class="info-grid">
									<div class="info-group">
										<label class="info-label">Name</label>
										<div class="info-value">{{ municipality.name }}</div>
									</div>
									<div class="info-group">
										<label class="info-label">Type</label>
										<div class="info-value">{{ municipality.type || '—' }}</div>
									</div>
									<div class="info-group">
										<label class="info-label">County</label>
										<div class="info-value">{{ municipality.county || '—' }}</div>
									</div>
									<div class="info-group">
										<label class="info-label">State</label>
										<div class="info-value">{{ municipality.state || '—' }}</div>
									</div>
									<div class="info-group">
										<label class="info-label">Population</label>
										<div class="info-value">{{ formatNumber(municipality.population) }}</div>
									</div>
									<div class="info-group">
										<label class="info-label">ZIP</label>
										<div class="info-value">{{ municipality.zip || '—' }}</div>
									</div>
									<div class="info-group">
										<label class="info-label">Phone</label>
										<div class="info-value">{{ municipality.phone || '—' }}</div>
									</div>
									<div class="info-group">
										<label class="info-label">Website</label>
										<div class="info-value">
											<a v-if="municipality.website" :href="municipality.website" target="_blank" rel="noopener">{{ municipality.website }}</a>
											<span v-else>—</span>
										</div>
									</div>
									<div class="info-group">
										<label class="info-label">Mail Returned Before</label>
										<div class="info-value">{{ municipality.mail_returned ? 'Yes' : 'No' }}</div>
									</div>
									<div class="info-group">
										<label class="info-label">Last Mail Date</label>
										<div class="info-value">{{ formatDate(municipality.last_mail_date) }}</div>
									</div>
								</div>
							</div>
						</div>

						<!-- Campaign & run -->
						<div class="data-section mb-4">
							<div class="section-header">
								<h3 class="section-title"><i class="fad fa-bullhorn me-2"></i> Campaign &amp; Run</h3>
							</div>
							<div class="section-content">
								<div class="info-grid">
									<div class="info-group">
										<label class="info-label">Campaign</label>
										<div class="info-value">{{ campaign.name || '—' }}</div>
									</div>
									<div class="info-group">
										<label class="info-label">Campaign Type</label>
										<div class="info-value text-capitalize">{{ campaign.type || '—' }}</div>
									</div>
									<div class="info-group">
										<label class="info-label">Run Code</label>
										<div class="info-value font-monospace">{{ run.code || '—' }}</div>
									</div>
									<div class="info-group">
										<label class="info-label">Run Status</label>
										<div class="info-value"><span class="status-badge" :class="run.status">{{ run.status }}</span></div>
									</div>
									<div class="info-group">
										<label class="info-label">Run Delivery Status</label>
										<div class="info-value"><span class="status-badge" :class="run.delivery_status">{{ (run.delivery_status || '').replace('_', ' ') }}</span></div>
									</div>
									<div class="info-group">
										<label class="info-label">Run Started</label>
										<div class="info-value">{{ formatDateTime(run.started_at) }}</div>
									</div>
								</div>
							</div>
						</div>

						<!-- Raw payload, for when a number does not add up -->
						<details v-if="hasLob" class="raw-section">
							<summary>Raw LOB payload</summary>
							<pre class="raw-json">{{ prettyLob }}</pre>
						</details>
					</div>
				</div>
			</div>
		</template>
	</div>
</template>

<script>
import restApi from '../../api/index'
import { formatUtc } from '../../utils/time'
import ShortlinkVisitTimeline from '@/components/campaign/ShortlinkVisitTimeline.vue'

export default {
	name: 'MunicipalityReport',
	components: { ShortlinkVisitTimeline },
	data() {
		return {
			item: {},
			municipality: null,
			client: null,
			campaign: {},
			run: {},
			lob: {},
			lobError: null,
			loading: false,
			loaded: false,
			loadError: null,
		}
	},
	computed: {
		hasLob() {
			return this.lob && Object.keys(this.lob).length > 0
		},
		thumbnails() {
			return this.lob.thumbnails || []
		},
		trackingEvents() {
			// LOB returns these oldest first; newest on top reads better here.
			return [...(this.lob.tracking_events || [])].reverse()
		},
		addressTo() {
			return this.lob.to || {}
		},
		addressFrom() {
			return this.lob.from || {}
		},
		recipientMoved() {
			return !!(this.lob.to && this.lob.to.recipient_moved)
		},
		itemCost() {
			const price = parseFloat(this.campaign.item_price)
			if (!price || !['sent', 'in_transit', 'delivered', 'returned'].includes(this.item.status)) return '—'
			return `$${price.toFixed(2)}`
		},
		prettyLob() {
			return JSON.stringify(this.lob, null, 2)
		},
	},
	mounted() {
		this.load()
	},
	methods: {
		async load() {
			this.loading = true
			this.loadError = null

			try {
				// The route's third param is the run item id, not a municipality id.
				const itemId = this.$route.params.municipalityId
				const response = await restApi.get(`/auto-campaign-run-items/${itemId}/lob-details`)

				this.item = response.data.item || {}
				this.municipality = response.data.municipality
				this.client = response.data.client
				this.campaign = response.data.campaign || {}
				this.run = response.data.run || {}
				this.lob = response.data.lob || {}
				this.lobError = response.data.lob_error
				this.loaded = true
			} catch (error) {
				console.error('Error loading mail piece details:', error)
				this.loadError = error.response?.data?.error || 'Could not load this mail piece.'
			} finally {
				this.loading = false
			}
		},
		thumbLabel(index) {
			if (this.lob.object === 'letter') return `Page ${index + 1}`
			return index === 0 ? 'Front' : 'Back'
		},
		eventClass(name) {
			const key = (name || '').toLowerCase()
			if (key === 'delivered') return 'is-delivered'
			if (key === 'returned to sender') return 'is-returned'
			return ''
		},
		// APIs serialize naive UTC, so both formatters go through the shared helpers.
		formatDateTime(value) {
			if (!value) return '—'
			return formatUtc(value, 'M/D/YYYY h:mm A')
		},
		formatDate(value) {
			if (!value) return '—'
			return formatUtc(value, 'M/D/YYYY')
		},
		formatNumber(value) {
			if (!value) return '—'
			return new Intl.NumberFormat().format(value)
		},
	},
}
</script>

<style scoped>
/* Import base styles from CreateAutoCampaign */
.municipality-report-container {
	min-height: calc(100vh - 200px);
	padding-bottom: 40px;
}

/* Header Styles */
.page-header {
	background: transparent;
	border: none;
	padding: 0;
}

.back-btn {
	width: 40px;
	height: 40px;
	display: flex;
	align-items: center;
	justify-content: center;
	background: #ffffff;
	border: 1px solid #e0e1e1;
	border-radius: 50%;
	color: #64748b;
	text-decoration: none;
	transition: all 0.2s ease;
	flex-shrink: 0;
}

.back-btn:hover {
	background: #f8f9fa;
	border-color: #d1d5db;
	color: #475569;
	text-decoration: none;
}

.page-title {
	font-size: 28px;
	font-weight: 700;
	color: #1f2937;
	margin: 0;
	line-height: 1.2;
}

.page-subtitle {
	font-size: 16px;
	color: #6b7280;
	margin: 4px 0 0 0;
	line-height: 1.4;
}

/* Preview Container Styles */
.preview-container {
	background: #ffffff;
	border-radius: 12px;
	padding: 24px;
	box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
	position: sticky;
	top: 20px;
}

.preview-title {
	font-size: 18px;
	font-weight: 600;
	color: #1f2937;
	margin-bottom: 16px;
	display: flex;
	align-items: center;
	gap: 8px;
}

.fallback-content,
.placeholder-content {
	text-align: center;
	color: #6b7280;
}

.pdf-actions {
	padding: 12px 16px;
	background: #ffffff;
	border-top: 1px solid #e5e7eb;
	display: flex;
	gap: 8px;
}

.image-placeholder {
	display: flex;
	align-items: center;
	justify-content: center;
	height: 100%;
	color: #9ca3af;
	text-align: center;
}

/* Data Container Styles */
.data-container {
	padding-left: 24px;
}

.data-section {
	background: #ffffff;
	border-radius: 12px;
	padding: 24px;
	box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
}

.section-header {
	margin-bottom: 20px;
	padding-bottom: 12px;
	border-bottom: 1px solid #e5e7eb;
}

.section-title {
	font-size: 18px;
	font-weight: 600;
	color: #1f2937;
	margin: 0;
	display: flex;
	align-items: center;
}

.section-content {
	color: #374151;
}

.info-group {
	margin-bottom: 16px;
}

.info-label {
	font-size: 12px;
	font-weight: 600;
	color: #6b7280;
	text-transform: uppercase;
	letter-spacing: 0.05em;
	margin-bottom: 4px;
	display: block;
}

.info-value {
	font-size: 14px;
	color: #1f2937;
	font-weight: 500;
	line-height: 1.4;
}

.font-monospace {
	font-family: 'Courier New', monospace;
}

/* Status Badges */
.status-badge {
	padding: 4px 8px;
	border-radius: 12px;
	font-size: 11px;
	font-weight: 500;
	text-transform: capitalize;
}

.status-badge.processed,
.status-badge.completed {
	background-color: #dcfce7;
	color: #166534;
}

.status-badge.delivered {
	background-color: #dbeafe;
	color: #1e40af;
}

.status-badge.failed {
	background-color: #fecaca;
	color: #dc2626;
}

.status-badge.pending,
.status-badge.processing {
	background-color: #fef3c7;
	color: #92400e;
}

.type-badge.letter {
	background-color: #dbeafe;
	color: #1e40af;
}

.type-badge.postcard {
	background-color: #e0f2fe;
	color: #0369a1;
}

/* Responsive adjustments */
@media (max-width: 768px) {
	.data-container {
		padding-left: 0;
		margin-top: 24px;
	}

	.preview-container {
		position: static;
	}
}

/* Header actions */
.header-actions {
	display: flex;
	align-items: center;
	gap: 10px;
}

.header-btn {
	display: inline-flex;
	align-items: center;
	gap: 6px;
	padding: 8px 14px;
	border: 1px solid #e5e7eb;
	background: #fff;
	border-radius: 999px;
	font-size: 13px;
	font-weight: 600;
	color: #374151;
	cursor: pointer;
	text-decoration: none;
	transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.header-btn:hover:not(:disabled) {
	background: #f3f4f6;
	border-color: #d1d5db;
	color: #111827;
	text-decoration: none;
}

.header-btn:disabled {
	opacity: 0.45;
	cursor: not-allowed;
}

/* States */
.loading-state {
	text-align: center;
	padding: 80px 0;
	color: #6b7280;
}

.loading-state i {
	font-size: 2rem;
	margin-bottom: 12px;
}

.lob-error-detail {
	display: block;
	font-size: 12px;
	opacity: 0.8;
	margin-top: 4px;
	word-break: break-word;
}

/* Mail piece thumbnails */
.preview-title {
	font-size: 16px;
	font-weight: 600;
	color: #1f2937;
	margin: 0 0 16px 0;
}

.thumb-list {
	display: flex;
	flex-direction: column;
	gap: 16px;
}

.thumb-label {
	font-size: 12px;
	font-weight: 600;
	color: #6b7280;
	text-transform: uppercase;
	letter-spacing: 0.04em;
	margin-bottom: 6px;
}

.thumb-link {
	display: block;
	border: 1px solid #e5e7eb;
	border-radius: 8px;
	overflow: hidden;
	background: #f9fafb;
}

.thumb-link img {
	display: block;
	width: 100%;
	height: auto;
}

.image-placeholder {
	border: 1px dashed #d1d5db;
	border-radius: 8px;
	padding: 40px 20px;
	text-align: center;
	color: #9ca3af;
}

.pdf-actions {
	display: flex;
	gap: 8px;
	margin-top: 16px;
	flex-wrap: wrap;
}

.asset-note {
	font-size: 11px;
	color: #9ca3af;
	margin: 8px 0 0 0;
}

/* Info grid */
.info-grid {
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
	gap: 16px;
}

.info-label {
	font-size: 12px;
	font-weight: 600;
	color: #6b7280;
	text-transform: uppercase;
	letter-spacing: 0.04em;
	margin-bottom: 4px;
	display: block;
}

.info-value {
	font-size: 14px;
	color: #1f2937;
	word-break: break-word;
}

.address-block {
	font-size: 14px;
	color: #1f2937;
	font-style: normal;
	margin: 6px 0 0 0;
	line-height: 1.6;
}

.error-box {
	margin-top: 16px;
	padding: 12px 14px;
	background: #fef2f2;
	border: 1px solid #fecaca;
	border-radius: 8px;
	font-size: 13px;
	color: #b91c1c;
	word-break: break-word;
}

.empty-note {
	font-size: 13px;
	color: #6b7280;
	margin: 0;
}

/* Timeline */
.timeline {
	list-style: none;
	margin: 0;
	padding: 0 0 0 22px;
	position: relative;
}

.timeline::before {
	content: '';
	position: absolute;
	left: 5px;
	top: 6px;
	bottom: 6px;
	width: 2px;
	background: #e5e7eb;
}

.timeline-item {
	position: relative;
	padding-bottom: 18px;
}

.timeline-item:last-child {
	padding-bottom: 0;
}

.timeline-dot {
	position: absolute;
	left: -22px;
	top: 4px;
	width: 12px;
	height: 12px;
	border-radius: 50%;
	background: #f59e0b;
	border: 2px solid #ffffff;
	box-shadow: 0 0 0 1px #e5e7eb;
}

.timeline-item.is-delivered .timeline-dot {
	background: #10b981;
}

.timeline-item.is-returned .timeline-dot {
	background: #6b7280;
}

.timeline-name {
	font-size: 14px;
	font-weight: 600;
	color: #1f2937;
}

.timeline-meta {
	font-size: 12px;
	color: #6b7280;
	margin-top: 2px;
}

.timeline-details {
	font-size: 12px;
	color: #4b5563;
	margin-top: 4px;
}

/* Raw payload */
.raw-section {
	background: #ffffff;
	border-radius: 12px;
	padding: 16px 24px;
	box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
	margin-bottom: 24px;
}

.raw-section summary {
	font-size: 13px;
	font-weight: 600;
	color: #6b7280;
	cursor: pointer;
}

.raw-json {
	margin: 12px 0 0 0;
	padding: 12px;
	background: #f9fafb;
	border: 1px solid #f3f4f6;
	border-radius: 8px;
	font-size: 11px;
	line-height: 1.5;
	color: #374151;
	max-height: 420px;
	overflow: auto;
	white-space: pre-wrap;
	word-break: break-word;
}

/* Status badges */
.status-badge {
	padding: 4px 10px;
	border-radius: 12px;
	font-size: 11px;
	font-weight: 600;
	text-transform: capitalize;
	background-color: #f3f4f6;
	color: #374151;
	display: inline-block;
}

.status-badge.delivered,
.status-badge.completed {
	background-color: #dcfce7;
	color: #166534;
}

.status-badge.in_transit,
.status-badge.sending {
	background-color: #fef3c7;
	color: #92400e;
}

.status-badge.error,
.status-badge.failed {
	background-color: #fecaca;
	color: #dc2626;
}

.status-badge.sent,
.status-badge.prepared,
.status-badge.pending {
	background-color: #dbeafe;
	color: #1e40af;
}

.status-badge.returned,
.status-badge.cancelled,
.status-badge.aborted {
	background-color: #f3f4f6;
	color: #4b5563;
}
</style>
